import { useState } from "react";
import { useSelector } from "react-redux";
import { Search, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";

const ConsultarDocumentos = () => {
  const user = useSelector((state) => state.user.user);
  const [documentNo, setDocumentNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const handleConsultar = async (e) => {
    e.preventDefault();
    if (!documentNo.trim()) {
      await notify("Insira o número do documento.", "warning");
      return;
    }

    try {
      setLoading(true);
      setResultado(null);

      const response = await api.post(`/documentos/consultar`, {
        documentNo: documentNo.trim(),
        empresa_id: user?.empresa_id,
      });

      // Extrai o conteúdo conforme o payload fornecido
      const data = response.data?.data || response.data;
      setResultado(data);
      
      await notify("Consulta efetuada com sucesso.", "success");
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível consultar a factura na AGT.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper para verificar se foi notificado (baseado no documentStatus ou sucesso da AGT)
  const getDocumentStatusInfo = (res) => {
    const agtDoc = res?.body?.agtResponse?.document;
    if (!agtDoc) return { notificado: false, texto: "Desconhecido", tipo: "secondary" };

    // Geralmente 'N' na AGT indica documento normal/notificado com sucesso, ou validado.
    const status = agtDoc.documentStatus;
    if (status === "N" || status === "Normal") {
      return { notificado: true, texto: "Documento Notificado / Válido (Status: N)", tipo: "success" };
    }
    return { notificado: false, texto: `Status na AGT: ${status}`, tipo: "warning" };
  };

  const canManage = ["admin", "operador", "caixa"].includes(user?.nivel);
  const statusInfo = resultado ? getDocumentStatusInfo(resultado) : null;
  const docDetails = resultado?.body?.agtResponse?.document;

  return (
    <ResourcePage
      title="Consulta de Factura"
      breadcrumb={["Documentos", "Consultar na AGT"]}
    >
      <div className="row justify-content-center">
        <div className="col-md-8">
          {/* Formulário de Pesquisa */}
          <div className="card app-card bg-white mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Pesquisar Documento</h5>
              
              {!canManage && (
                <div className="alert alert-warning py-2 mb-3">
                  Não tem permissões para consultar documentos.
                </div>
              )}

              <form onSubmit={handleConsultar}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="documentNo">
                    Número do Documento (Ex: FR FR8026S6656N/3)
                  </label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      id="documentNo"
                      value={documentNo}
                      onChange={(e) => setDocumentNo(e.target.value)}
                      placeholder="Digite o número exacto da factura..."
                      required
                    />
                    <button
                      className="btn btn-primary d-inline-flex align-items-center gap-1"
                      type="submit"
                      disabled={loading || !canManage}
                    >
                      <Search size={16} />
                      {loading ? "A consultar..." : "Consultar"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Resultado da Consulta Exibido na Mesma Página */}
          {resultado && (
            <div className="card app-card bg-white shadow-sm border-0">
              <div className="card-header bg-light py-3">
                <h6 className="m-0 font-weight-bold text-dark d-flex align-items-center gap-2">
                  <FileText size={18} /> Detalhes da Resposta AGT
                </h6>
              </div>
              <div className="card-body">
                {/* Badge de Status de Notificação */}
                <div className={`alert alert-${statusInfo.tipo} d-flex align-items-center gap-2 mb-4`}>
                  {statusInfo.notificado ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                  <div>
                    <strong>Estado da Notificação:</strong> {statusInfo.texto}
                  </div>
                </div>

                {docDetails ? (
                  <div className="row g-3">
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">Número do Documento</p>
                      <p className="fw-bold">{docDetails.documentNo}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">Tipo</p>
                      <p className="fw-bold">{docDetails.documentType}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">Data de Emissão</p>
                      <p className="fw-bold">{docDetails.documentDate}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">NIF da Empresa</p>
                      <p className="fw-bold">{docDetails.taxRegistrationNumber}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">Nome da Empresa</p>
                      <p className="fw-bold">{docDetails.companyName}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1 text-muted small">Total Geral</p>
                      <p className="fw-bold text-primary">
                        {Number(docDetails.documentTotals?.grossTotal || 0).toLocaleString()} Kz
                      </p>
                    </div>
                  </div>
                ) : (
                  <pre className="bg-light p-3 rounded small text-muted">
                    {JSON.stringify(resultado, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ResourcePage>
  );
};

export default ConsultarDocumentos;
